import React, { ReactElement, useEffect, useState } from 'react';
import { FrownOutlined } from '@ant-design/icons';
import { Result } from 'antd';
import { useTranslation } from 'react-i18next';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Space } from 'common/components/antd/Space/Space';
import { Body, Footer, Header, EnhancedModal } from 'common/components/EnhancedModal/EnhancedModal';
import { EnhancedSelect } from 'common/components/EnhancedSelect/EnhancedSelect';
import { IS_BANK_FE } from 'common/constants/general-access-role.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { notifySuccess } from 'common/utils/notifier.utils';
import { useCreateRoleInBranchRoleMutation } from 'domains/branches/queries/branch.query';
import {
	TCounterpartyAvailableBranchEntity,
	TCounterpartyAvailableBranchesResData,
} from 'domains/branches/types/branch.types';
import { TCounterpartiesListResData, TCounterpartyEntity } from 'domains/counterparties/types/counterparty.types';
import {
  SControl,
  SLabel,
  SFooter,
} from './modal.styles';
import { TCreateRoleModalData } from '../types/branch-roles.types';

type TProps = {
  isOpen: boolean;
  onCancelModalHandler: () => void;
  data: TCreateRoleModalData;
};

enum EFormDataNames {
	CounterpartyGuid = 'counterparty_guid',
	BranchGuid = 'branch_guid',
	RoleName = 'role_name',
}

const CreateRoleModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelModalHandler,
    data: {
      counterpartiesData,
	    branchesData,
	    selectedCounterparty,
	    selectedBranch,
    },
  } = props;
  const { t } = useTranslation();
	const [form] = Form.useForm();
  const [isSubmitBtnLoading, setIsSubmitBtnLoading] = useState<boolean>(false);

  const { mutateAsync: createRoleInBranchRoleMutation } = useCreateRoleInBranchRoleMutation();

	useEffect(() => {
		form.setFields([
			{
				name: [EFormDataNames.CounterpartyGuid],
				value: selectedCounterparty,
			},
			{
				name: [EFormDataNames.BranchGuid],
				value: selectedBranch,
			}
		]);
	}, [selectedCounterparty, selectedBranch]);

  const counterpartyOptions = ((counterpartiesData as TCounterpartiesListResData)?.counterparties || [])
    .map((counterparty: TCounterpartyEntity) => ({
      value: counterparty.guid,
      label: counterparty.name,
    }));

	const branchesOptions = ((branchesData as TCounterpartyAvailableBranchesResData)?.branches || [])
		.map((counterparty: TCounterpartyAvailableBranchEntity) => ({
			value: counterparty.guid,
			label: counterparty.name,
		}));

  const submitFormHandler = async (values: Record<EFormDataNames, string>): Promise<void> => {
    try {
      setIsSubmitBtnLoading(true);
      const { counterparty_guid, role_name, branch_guid } = values;

      await createRoleInBranchRoleMutation({
        counterpartyGUID: counterparty_guid,
        requestData: { role_name, branch_guid },
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.BranchQueryKeys].getPermissionTableByBranch._def,
      });
      setIsSubmitBtnLoading(false);
      notifySuccess('usrRoleMng.branchRoles.create.notification.success');
      onCancelModalHandler();
    } catch (error) {
      console.error(error);
      setIsSubmitBtnLoading(false);
    }
  };

  return (
    <EnhancedModal open={isOpen} width={480} onCancel={onCancelModalHandler}>
      <Form
	      form={form}
        onFinish={submitFormHandler}
      >
        <Header>{t('usrRoleMng.branchRoles.modal.create.createRole.title')}</Header>

        {branchesOptions.length ? (
          <>
            <Body>
              <Space size={24} direction="vertical">
                <div>
                  <SLabel>{t('usrRoleMng.branchRoles.modal.create.name.label')}</SLabel>

                  <SControl>
                    <Form.Item noStyle name={EFormDataNames.RoleName}>
                      <Input
                        size="large"
                        placeholder={t('usrRoleMng.branchRoles.modal.create.name.placeholder') as string}
                      />
                    </Form.Item>
                  </SControl>
                </div>

                <div>
                  <SControl>
                    <Form.Item
                      label={t('usrRoleMng.branchRoles.modal.create.branch.label')}
                      name={EFormDataNames.BranchGuid}
                    >
                      <EnhancedSelect
                        style={{ width: '100%' }}
                        size="large"
                        disabled
                        placeholder={t('usrRoleMng.branchRoles.modal.create.selectBranch.placeholder') as string}
                        options={branchesOptions}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('usrRoleMng.branchRoles.modal.create.counterparty.title')}
                      name={EFormDataNames.CounterpartyGuid}
                      hidden={IS_BANK_FE}
                    >
                      <EnhancedSelect
                        style={{
                          width: '100%',
                        }}
                        disabled
                        size="large"
                        placeholder={t('usrRoleMng.branchRoles.modal.create.counterparty.placeholder')}
                        options={counterpartyOptions}
                      />
                    </Form.Item>
                  </SControl>
                </div>
              </Space>
            </Body>
            <Footer>
              <SFooter>
                <Button
                  block
                  style={{
                    marginRight: 12,
                  }}
                  onClick={onCancelModalHandler}
                  size="large"
                >
                  {t('usrRoleMng.branchRoles.modal.create.button.cancel')}
                </Button>
                <Button block loading={isSubmitBtnLoading} htmlType="submit" type="primary" size="large">
                  {t('usrRoleMng.branchRoles.modal.create.button.create')}
                </Button>
              </SFooter>
            </Footer>
          </>
        ) : (
          <Result
            icon={<FrownOutlined />}
            title={t('usrRoleMng.branchRoles.modal.create.result.text')}
          />
        )
        }
      </Form>
    </EnhancedModal>
  );
};

export default CreateRoleModal;
